import Vue from 'vue'
import { forEach, cloneDeep } from 'lodash'

import plugins from '@/plugins'
import config from '@/../config'
import store from '@/vuex/store'

Vue.use(plugins)

// ---------------------------
//  API Client Base Prototype
// ---------------------------
export function HTTP () {
  const self = this

  this.apiURl = config.local.API_URL.replace(/"(.*)"/, '$1')

  this.resource = Vue.axios.create()

  // Axios does not extends the global/main interceptor behaviour (that means
  // we will have to import or set the interceptor for each instance). There is a PR around this issue
  // https://github.com/axios/axios/issues/993
  this.resource.interceptors.request.use(function (req) {
    const token = store.getters['auth/token']

    if (token) {
      req.headers.common = Object.assign({}, req.headers.common, { Authorization: `Bearer ${token}` })
    }

    return req
  }, function (error) {
    // Do something with request error
    return Promise.reject(error)
  })

  this.relativeUrl = ''

  this.routes = {
    index () { return '' },

    store () { return '' },

    show (id) { return '/' + id },

    update (id) { return '/' + id },

    destroy (id) { return '/' + id },

    relativeMedia (id) {
      return id !== undefined ? `${self.relativeUrl}/${id}/media` : `/media`
    },

    uploadMedia (id) {
      return self.apiURl + self.routes.relativeMedia(id)
    },

    uploadFile (id) {
      return self.apiURl + (id !== undefined ? `${self.relativeUrl}/${id}/files` : `/files`)
    },

    destroyFile (id, fileId) {
      return self.apiURl + (id !== undefined ? `${self.relativeUrl}/${id}/files/${fileId}` : `/files/${fileId}`)
    }
  }

  this.getRoute = (name, data) => {
    if (self.routes[name]) {
      return self.relativeUrl + self.routes[name](data)
    }
  }

  this.defaultUrl = () => {
    return self.resource.defaults.baseURL
  }

  this.setUrl = (url) => {
    self.resource.defaults.baseURL = url

    return self
  }

  this.getUrl = () => {
    return self.resource.defaults.baseURL
  }

  this.appendToUrl = (url) => {
    self.relativeUrl = url

    self.resource.defaults.baseURL += url

    return self
  }

  this.index = (options) => {
    return self.resource.get(self.routes.index(), self.parseOptions(options))
  }

  this.store = (formData, options) => {
    return self.resource.post(self.routes.store(), formData, self.parseOptions(options))
  }

  this.show = (id, options) => {
    return self.resource.get(self.routes.show(id), self.parseOptions(options))
  }

  this.destroy = (id, options) => {
    return self.resource.delete(self.routes.destroy(id), self.parseOptions(options))
  }

  this.update = (id, options) => {
    return self.resource.put(self.routes.update(id), self.parseOptions(options))
  }

  /**
   * Specific file related methods
   */
  this.destroyFile = (id, fileId, options) => {
    return self.resource.delete(self.routes.destroyFile(id, fileId), options)
  }

  this.export = (query, options) => {
    return self.resource.get(self.routes.export(query), self.parseOptions(options))
  }

  /**
   * Parse the options on request to be compatible with the RequestCriteria
   */
  this.parseOptions = (options) => {
    const newOptions = cloneDeep(options)

    if (newOptions && newOptions.params) {
      const params = newOptions.params

      if (params) {
        if ('with' in params) {
          if (params.with.constructor === String) {
            params.with = params.with.split(';')
          }

          if (params.with.constructor === Array) {
            params._with = params.with.join(';')
          }

          delete params['with']
        }

        if ('orderBy' in params) {
          if (params.orderBy.constructor === String) {
            params._order = params.orderBy
          } else if (params.orderBy.constructor === Object) {
            if (params.orderBy.name) {
              params._order = params.orderBy.name + ':' + (params.orderBy.direction || 'asc')
            }
          }

          delete params['orderBy']
        }

        if ('search' in params) {
          if (params.search.constructor === String && params.search !== '') {
            params._search = params.search
          } else if (params.search.constructor === Object || params.search.constructor === Array) {
            let search = []

            forEach(params.search, (value, key) => {
              if (value) {
                if (value.constructor === String && value !== '') {
                  search.push(value)
                } else {
                  if (value.constructor === Object || value.constructor === Array) {
                    forEach(value, (value, key) => {
                      if (value !== '') {
                        search.push(`${key}:${value}`)
                      }
                    })
                  } else {
                    if (value !== '') {
                      search.push(`${key}:${value}`)
                    }

                    if (value.constructor === String) {
                      search.push(value)
                    } else {
                      search.push(`${key}:${value}`)
                    }
                  }
                }
              }
            })

            params._search = search
          }

          delete params['search']
        }

        if ('scopes' in params) {
          if (params.scopes.constructor === String) {
            params._scopes = params.scopes
          } else if (params.scopes.constructor === Object || params.scopes.constructor === Array) {
            let scopes = []

            forEach(params.scopes, (value, key) => {
              if (value.constructor === String) {
                scopes.push(value)
              } else {
                scopes.push(`${key}:${value}`)
              }
            })

            params._scope = scopes
          }

          delete params['scopes']
        }

        if (params.append && params.append.constructor === String) {
          params.append = params.append.split(';')
        }
      }
    }

    return newOptions
  }
};

export function resolveHTTP (url) {
  const http = Object.create(new HTTP())

  http.resetUrl = () => {
    http.appendToUrl(url)
  }
  http.resetUrl()

  return http
}

export default HTTP



// WEBPACK FOOTER //
// ./src/services/api/index.js